/**
 * Copyright (c) 2024-2026 Ronan LE MEILLAT
 * License: AGPL-3.0-or-later
 */

const FALLBACK_IMG = "https://placehold.co/400x400/1a1a1a/666?text=No+Image";

interface ProductImageProps {
  src?: string | null;
  alt: string;
  /** When > 1, renders an "N options" badge. */
  variantCount?: number;
  /** Optional click handler — adds cursor-pointer + role/tabIndex accessibility. */
  onClick?: () => void;
  /**
   * On-model shot to crossfade into on hover. Card-grid use only — see
   * `hoverZoom` for the product detail page's main image, which never swaps.
   * When omitted (or null) and `hoverZoom` is not set, there is no hover
   * effect at all — the absence of a worn shot is meant to be visible rather
   * than papered over with a generic zoom.
   */
  wornSrc?: string | null;
  /**
   * Plain zoom-on-hover, independent of `wornSrc`. Used by the product detail
   * page's main image, which always zooms and never swaps to a worn shot.
   */
  hoverZoom?: boolean;
}

/**
 * Square product image with a fallback placeholder on error and an optional
 * "N options" badge when multiple variants exist. Hover behavior is opt-in
 * and mutually exclusive: pass `wornSrc` for the card-grid crossfade-to-worn
 * swap, or `hoverZoom` for the detail page's plain zoom. Passing neither
 * means no hover effect.
 */
export function ProductImage({ src, alt, variantCount, onClick, wornSrc, hoverZoom }: ProductImageProps) {
  const interactive = onClick !== undefined;
  const hasWorn = !!wornSrc;

  return (
    <div
      className={`group bg-white aspect-square rounded-xl overflow-hidden mb-4 relative${interactive ? " cursor-pointer" : ""}`}
      onClick={onClick}
      role={interactive ? "button" : undefined}
      tabIndex={interactive ? 0 : undefined}
      onKeyDown={interactive ? (e) => { if (e.key === "Enter" || e.key === " ") onClick(); } : undefined}
    >
      <img
        src={src || FALLBACK_IMG}
        alt={alt}
        className={`w-full h-full object-cover transition-transform duration-300${hoverZoom ? " group-hover:scale-105" : ""}${hasWorn ? " absolute inset-0 transition-opacity group-hover:opacity-0" : ""}`}
        onError={(e) => {
          (e.target as HTMLImageElement).src = FALLBACK_IMG;
        }}
      />
      {hasWorn && (
        <img
          src={wornSrc || FALLBACK_IMG}
          alt={`${alt} — worn`}
          className="w-full h-full object-cover absolute inset-0 opacity-0 transition-opacity group-hover:opacity-100"
          onError={(e) => {
            (e.target as HTMLImageElement).src = FALLBACK_IMG;
          }}
        />
      )}
      {variantCount !== undefined && variantCount > 1 && (
        <span className="absolute top-3 left-3 bg-black/70 text-white text-xs px-2 py-1 rounded">
          {variantCount} options
        </span>
      )}
    </div>
  );
}
